import clsx from 'clsx'
import {HTMLProps} from 'react'
import {useInView} from 'react-intersection-observer/useInView'
import defaultImageLoader from '../../../lib/default-image-loader'
import blurredBackground from '../../../public/assets/blurred-gradient-background.svg'
import {JobOpening} from '../../../types'
import Container from '../../atoms/container'
import Heading from '../../atoms/heading'
import LoadableImage from '../../atoms/loadable-image'
import OpenPositionCard from '../open-position-card'

export interface OpenPositionSectionProps
  extends Omit<HTMLProps<HTMLDivElement>, 'ref'> {
  /**
   * List of open positions to be displayed here.
   */
  openPositions: Array<JobOpening>
  /**
   * Title of this section. Pass a translated value.
   */
  title?: string
  /**
   * Determines whether or not the gradient background should be hidden or not.
   */
  hideGradientBackground?: boolean
}

export function OpenPositionSection({
  openPositions,
  title,
  className,
  children,
  hideGradientBackground,
  ...props
}: OpenPositionSectionProps) {
  const {inView, ref} = useInView()

  return (
    <Container
      component="section"
      className={clsx(
        'relative grid justify-items-center gap-6 md:gap-8',
        className,
      )}
      ref={ref}
      {...props}
    >
      {title && (
        <Heading variant="h2" disableMaxWidth className="text-center z-10">
          {title}
        </Heading>
      )}

      <div className="grid gap-6 justify-self-stretch z-10">
        {openPositions.map(openPosition => (
          <OpenPositionCard
            openPosition={openPosition}
            key={openPosition.title}
          />
        ))}
      </div>

      <div className="z-10">{children}</div>

      {!hideGradientBackground && (
        <div
          className={clsx(
            'absolute -bottom-32 mx-auto -right-3/4 sm:-right-1/2 md:-right-24 z-0',
            inView ? 'motion-safe:animate-fade-in' : 'opacity-0',
          )}
        >
          <LoadableImage
            src={blurredBackground}
            layout="fixed"
            alt="Blurred patches with gradient background"
            loader={defaultImageLoader}
            className="object-left-bottom"
            placeholderColor="transparent"
            wrapperClassName={clsx(
              inView &&
                'motion-safe:animate-translate-horizontally-back-and-forth',
            )}
            disableDefaultAnimation
          />
        </div>
      )}
    </Container>
  )
}

export default OpenPositionSection
